/**
 * SpeedStreaks.ts — the boost's screen-space rush (M145, owner: "add more of
 * a zooming boost effect"). The M14 layer it replaces was a static striped
 * gradient that only faded in; this one MOVES: streaks are born around a
 * clear window in the middle of the screen and fly outward, speeding up and
 * stretching as they go, the way scenery does when you punch forward.
 *
 * One small 2D canvas over the game (never above the HUD readouts), drawn
 * only while a boost is live — when idle it is hidden and costs nothing. The
 * canvas is kept at a modest resolution on purpose: the streaks are soft
 * light, and a phone must not pay for a second full-resolution layer.
 */

const COUNT = 84;
/** Backing-store cap (px on the long side). */
const MAX_BACKING = 1100;
/** Where the road ahead sits under the chase camera (fractions of the screen). */
const VANISH_X = 0.5;
const VANISH_Y = 0.46;

interface Streak {
  angle: number;
  r: number; // 0 = the vanishing point, 1 = the screen edge
  speed: number;
  len: number;
  width: number;
}

export class SpeedStreaks {
  private readonly canvas: HTMLCanvasElement;
  private readonly flash: HTMLDivElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly streaks: Streak[] = [];
  private level = 0;
  private on = false;
  private shown = false;
  private cssW = 0;
  private cssH = 0;

  constructor(parent: HTMLElement) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'hud-speedlines';
    this.canvas.style.display = 'none';
    parent.appendChild(this.canvas);
    this.flash = document.createElement('div');
    this.flash.className = 'hud-boostflash';
    parent.appendChild(this.flash);
    this.ctx = this.canvas.getContext('2d');
    for (let i = 0; i < COUNT; i++) this.streaks.push(this.spawn(Math.random()));
  }

  private spawn(r: number): Streak {
    return {
      angle: Math.random() * Math.PI * 2,
      r: 0.22 + r * 0.85,
      speed: 0.9 + Math.random() * 1.3,
      len: 0.1 + Math.random() * 0.22,
      width: 0.9 + Math.random() * 2.1,
    };
  }

  /** Call every race frame. `on` = a boost is live (and motion is allowed). */
  update(on: boolean, dt: number): void {
    if (on && !this.on) this.ignite();
    this.on = on;
    this.level += ((on ? 1 : 0) - this.level) * (1 - Math.exp(-(on ? 14 : 5) * dt));
    if (this.level < 0.01) {
      if (this.shown) {
        this.shown = false;
        this.canvas.style.display = 'none';
      }
      return;
    }
    if (!this.shown) {
      this.shown = true;
      this.canvas.style.display = '';
    }
    this.canvas.style.opacity = this.level.toFixed(3);
    this.draw(dt);
  }

  /** Drop everything now (the HUD is being hidden). */
  reset(): void {
    this.on = false;
    this.level = 0;
    this.shown = false;
    this.canvas.style.display = 'none';
    this.flash.classList.remove('go');
  }

  /** The instant the boost lights: one white-blue flash off the screen edges. */
  private ignite(): void {
    this.flash.classList.remove('go');
    void this.flash.offsetWidth; // restart the animation
    this.flash.classList.add('go');
  }

  private draw(dt: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (w !== this.cssW || h !== this.cssH) {
      this.cssW = w;
      this.cssH = h;
      const k = Math.min(1, MAX_BACKING / Math.max(w, h));
      this.canvas.width = Math.max(1, Math.round(w * k));
      this.canvas.height = Math.max(1, Math.round(h * k));
    }
    const cw = this.canvas.width;
    const ch = this.canvas.height;
    const cx = cw * VANISH_X;
    const cy = ch * VANISH_Y;
    // An ellipse a little bigger than the screen: r = 1 is past every edge.
    const rx = cw * 0.62;
    const ry = ch * 0.66;
    const px = cw / 1000; // line widths scale with the canvas
    ctx.clearRect(0, 0, cw, ch);
    ctx.lineCap = 'round';
    for (let i = 0; i < this.streaks.length; i++) {
      let s = this.streaks[i];
      // Outward, and faster the further out it is — that is the perspective.
      s.r += s.speed * dt * (0.3 + s.r * 1.9);
      if (s.r > 1.12) s = this.streaks[i] = this.spawn(0);
      // Nothing in the driving window; full strength toward the edges.
      const edge = Math.min(1, Math.max(0, (s.r - 0.26) / 0.4));
      if (edge <= 0) continue;
      const cos = Math.cos(s.angle);
      const sin = Math.sin(s.angle);
      const r2 = s.r + s.len * (0.35 + s.r * 1.3);
      ctx.globalAlpha = 0.72 * edge * edge;
      ctx.strokeStyle = i % 3 === 0 ? '#aee9ff' : '#ffffff';
      ctx.lineWidth = s.width * (0.8 + s.r * 2.4) * px;
      ctx.beginPath();
      ctx.moveTo(cx + cos * rx * s.r, cy + sin * ry * s.r);
      ctx.lineTo(cx + cos * rx * r2, cy + sin * ry * r2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }
}
